package com.royceecoleman.bill.shared.billing

import kotlinx.serialization.Serializable

@Serializable
data class ModelPolicy(
    val modelId: String,
    val reasoning: String = "none",
    val maxOutputTokens: Int = 16000,
    val inputUsdPerMillion: String = "",
    val outputUsdPerMillion: String = "",
    val revision: Int = 0,
)

@Serializable
data class OperationsPolicy(
    val draftModel: String = "",
    val guideModel: String = "",
    /** The rules check after drafting; blank follows the draft model. */
    val reviewModel: String = "",
    val revision: Int = 0,
    val reason: String = "",
    val models: List<ModelPolicy> = emptyList(),
)

@Serializable
data class AiCallView(
    val id: String,
    val owner: Int,
    val kind: String,
    val model: String,
    val status: String,
    val startedAt: Long,
    val durationMs: Long,
    val inputTokens: Long,
    val outputTokens: Long,
    val cachedTokens: Long,
    val reasoningTokens: Long,
    val costUsd: String,
    val costBasis: String,
)

@Serializable
data class UsageGroup(
    val label: String,
    val calls: Long,
    val inputTokens: Long,
    val outputTokens: Long,
    val reportedUsd: String,
    val estimatedUsd: String,
    val unknownCosts: Long,
)

@Serializable
data class ActivityCount(
    val label: String,
    val count: Long,
)

@Serializable
data class OperationsOverview(
    val from: String,
    val total: UsageGroup,
    val models: List<UsageGroup>,
    val days: List<UsageGroup>,
    val activeAiUsers: Long,
    val activities: List<ActivityCount>,
    val calls: List<AiCallView>,
    val moreCalls: Boolean,
    /** How closely lawyers kept each model's drafts, from approvals in the report period. */
    val quality: List<QualityGroup> = emptyList(),
)

@Serializable
data class QualityGroup(
    val label: String,
    val approvedDrafts: Long,
    val unchanged: Long,
    val meanSimilarity: Double,
    val taskCodeKept: Long,
    val activityCodeKept: Long,
)

@Serializable
data class AdminAction(
    val action: String,
    val reason: String,
    val password: String = "",
)

@Serializable
data class AuditItem(
    val at: Long,
    val actor: Int,
    val target: String,
    val action: String,
    val reason: String,
)

@Serializable
data class AccountView(
    val id: Int,
    val username: String,
    val role: String,
    val locked: Boolean,
    val createdAt: Long,
    val lastSeenAt: Long,
)

@Serializable
data class AccountDirectory(
    val users: List<AccountView>,
    val totalCustomers: Long,
    val newCustomers30d: Long,
    val activeCustomers30d: Long,
    val more: Boolean,
)

@Serializable
data class GrantRequest(
    val requestId: String,
    val months: Int,
    val reason: String,
)

@Serializable
data class GrantView(
    val id: String,
    val until: Long,
    val revoked: Boolean,
    val reason: String,
    val actor: Int,
)

/** Adds [credits] drafting credits to an account; [requestId] makes a retried request a no-op. */
@Serializable
data class CreditGrantRequest(
    val requestId: String,
    val credits: Int,
    val reason: String,
)

@Serializable
data class CreditGrantView(
    val id: String,
    val credits: Int,
    val at: Long,
    val reason: String,
    val actor: Int,
)

@Serializable
data class AccountBillingView(
    val access: BillingAccess,
    val grants: List<GrantView>,
    val stripeCustomer: String,
    val creditGrants: List<CreditGrantView> = emptyList(),
)

@Serializable
data class SupportMessage(
    val id: String,
    val at: Long,
    val fromSupport: Boolean,
    val body: String,
)

@Serializable
data class SupportTicket(
    val id: String,
    val owner: Int,
    val subject: String,
    val status: String,
    val revision: Int,
    val updatedAt: Long,
    val messages: List<SupportMessage> = emptyList(),
)

@Serializable
data class SupportList(
    val tickets: List<SupportTicket>,
    val more: Boolean,
)

@Serializable
data class SupportCreate(
    val requestId: String,
    val subject: String,
    val message: String,
)

@Serializable
data class SupportReply(
    val requestId: String,
    val revision: Int,
    val message: String,
    val status: String = "OPEN",
)
